import { blue, grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import type { ConnectedEquipment } from "verdi-common/schemas/device/device";
import { Icon } from "@/components/icon/icon";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS } from "@/icons/icon-registry";
import { IRRIGATION_STATE } from "@/services/device-attributes/attributes/irrigation/definitions";

/**
 * Makes a string representation of a date
 * eg. 12/31/2024 12:00 PM or if same day just the time eg. 12:00 PM
 */
export function makeDateTimeString(date: Date) {
    // if date is on current day, do not show the month or day
    const isToday = date.toLocaleDateString() === new Date().toLocaleDateString();
    return `${date.toLocaleString("default", {
        month: isToday ? undefined : "short",
        day: isToday ? undefined : "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    })}`;
}

const ICON_COLOR = blue[500];
const CLOSING_ICON_COLOR = grey[900];
const ICON_CLOSED_COLOR = grey[500];

export enum IconType {
    Static = "static",
    Irrigating = "irrigating",
    Transitioning = "transitioning",
}

export const IRRIGATION_STATE_COLORS = {
    [IRRIGATION_STATE.OPENING]: {
        iconColor: ICON_COLOR,
        textColor: ICON_COLOR,
        iconClassName: "text-[#2196f3]",
        textClassName: "text-[#2196f3]",
    },
    [IRRIGATION_STATE.CLOSING]: {
        iconColor: CLOSING_ICON_COLOR,
        textColor: CLOSING_ICON_COLOR,
        iconClassName: "text-[#212121]",
        textClassName: "text-[#212121]",
    },
    [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: {
        iconColor: ICON_COLOR,
        textColor: ICON_COLOR,
        iconClassName: "text-[#2196f3]",
        textClassName: "text-[#2196f3]",
    },
    [IRRIGATION_STATE.OPEN]: {
        iconColor: ICON_COLOR,
        textColor: ICON_COLOR,
        iconClassName: "text-[#2196f3]",
        textClassName: "text-[#2196f3]",
    },
    [IRRIGATION_STATE.CLOSED]: {
        iconColor: ICON_CLOSED_COLOR,
        textColor: undefined,
        iconClassName: "text-[#9e9e9e]",
        textClassName: undefined,
    }, // undefined means no text color
} as const;

export const IRRIGATION_ICON_TRANSLATION_NAMESPACE = NAMESPACES.ICONS;
export const IRRIGATION_ICON_TRANSLATION_KEY_PREFIX = "irrigation-status";

export const irrigationStateIconKeys = {
    [IRRIGATION_STATE.OPENING]: ICON_KEYS.REFRESH, // Sizing inconsistency: the RefreshCw icon is larger than the other icons by default from Lucide
    [IRRIGATION_STATE.CLOSING]: ICON_KEYS.REFRESH,
    [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: ICON_KEYS.SCHEDULE,
    [IRRIGATION_STATE.OPEN]: ICON_KEYS.VALVE_OPEN,
    [IRRIGATION_STATE.CLOSED]: ICON_KEYS.VALVE_CLOSED,
} as const;

export const irrigationStateTooltipTranslationKeys = {
    [IRRIGATION_STATE.OPEN]: "tooltip.open",
    [IRRIGATION_STATE.CLOSED]: "tooltip.closed",
    [IRRIGATION_STATE.OPENING]: "tooltip.opening",
    [IRRIGATION_STATE.CLOSING]: "tooltip.closing",
    [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: "tooltip.scheduled-irrigation",
};

export const pumpStateTooltipTranslationKeys = {
    [IRRIGATION_STATE.OPEN]: "pump.tooltip.open",
    [IRRIGATION_STATE.CLOSED]: "pump.tooltip.closed",
    [IRRIGATION_STATE.OPENING]: "pump.tooltip.opening",
    [IRRIGATION_STATE.CLOSING]: "pump.tooltip.closing",
    [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: "pump.tooltip.scheduled-irrigation",
};

export const irrigationStateLabelTranslationKeys = {
    [IRRIGATION_STATE.OPEN]: "label.open",
    [IRRIGATION_STATE.CLOSED]: "label.closed",
    [IRRIGATION_STATE.OPENING]: "label.opening",
    [IRRIGATION_STATE.CLOSING]: "label.closing",
    [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: "label.scheduled-irrigation",
};

export const pumpStateLabelTranslationKeys = {
    [IRRIGATION_STATE.OPEN]: "pump.label.open",
    [IRRIGATION_STATE.CLOSED]: "pump.label.closed",
    [IRRIGATION_STATE.OPENING]: "pump.label.opening",
    [IRRIGATION_STATE.CLOSING]: "pump.label.closing",
    [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: "pump.label.scheduled-irrigation",
};

interface IrrigationStatusIconProps {
    irrigationState: IRRIGATION_STATE;
    percentageScheduledIrrigationCompleted?: number;
    irrigationEndTime?: Date;
    includeLabel?: boolean;
    includeTooltip?: boolean;
    connectedEquipment?: ConnectedEquipment;
}

/**
 * IrrigationStatusIcon displays the irrigation (valve) state of a device
 */
export function IrrigationStatusIcon({
    irrigationState,
    percentageScheduledIrrigationCompleted,
    irrigationEndTime,
    includeLabel = true,
    includeTooltip = true,
    connectedEquipment,
}: IrrigationStatusIconProps) {
    const { t } = useTranslation(NAMESPACES.ICONS, { keyPrefix: IRRIGATION_ICON_TRANSLATION_KEY_PREFIX });

    const isPump = connectedEquipment === "pump";
    const baseColors = IRRIGATION_STATE_COLORS[irrigationState];
    const { iconClassName, textClassName } = baseColors;

    const irrigationStateTooltipParams = {
        [IRRIGATION_STATE.OPEN]: {},
        [IRRIGATION_STATE.CLOSED]: {},
        [IRRIGATION_STATE.OPENING]: {},
        [IRRIGATION_STATE.CLOSING]: {},
        [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: {
            percentageScheduledIrrigationCompleted: percentageScheduledIrrigationCompleted ?? 0,
            irrigationEndTime: makeDateTimeString(irrigationEndTime ?? new Date()),
        },
    };

    const tooltipKeys = isPump ? pumpStateTooltipTranslationKeys : irrigationStateTooltipTranslationKeys;
    const labelKeys = isPump ? pumpStateLabelTranslationKeys : irrigationStateLabelTranslationKeys;
    const tooltip = includeTooltip
        ? t(tooltipKeys[irrigationState], irrigationStateTooltipParams[irrigationState])
        : undefined;
    const label = includeLabel ? t(labelKeys[irrigationState]) : undefined;
    return (
        <Icon
            icon={irrigationStateIconKeys[irrigationState]}
            label={includeLabel ? label : undefined}
            tooltip={includeTooltip ? tooltip : undefined}
            iconClassName={iconClassName}
            labelClassName={textClassName}
        />
    );
}
